import { useEffect, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query, type Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import type { PublicRoom } from './types';

// A lobby with no activity for this long is treated as abandoned and hidden —
// the projection has no heartbeat, so freshness is our only "is it alive" hint.
const FRESH_MS = 20 * 60 * 1000;

/**
 * Live browser of open public lobbies (the `publicRooms` projection the
 * `syncPublicRoom` trigger maintains). Subscribes only while `active` — pass
 * the modal's open state so we don't hold a listener on the whole home screen.
 * Freshness is re-evaluated on a timer so stale rooms drop out even if no new
 * snapshot arrives.
 */
export function usePublicRooms(active: boolean): PublicRoom[] {
  const [rooms, setRooms] = useState<PublicRoom[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!active) {
      setRooms([]);
      return;
    }
    const q = query(collection(db, 'publicRooms'), orderBy('updatedAt', 'desc'), limit(40));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRooms(
          snap.docs.map((doc) => {
            const d = doc.data();
            const updatedAt = d.updatedAt as Timestamp | undefined;
            return {
              roomId: (d.roomId as string) ?? doc.id,
              code: (d.code as string) ?? '',
              status: (d.status as 'lobby' | 'playing') ?? 'lobby',
              hostName: (d.hostName as string) ?? 'Runner',
              hostCountry: (d.hostCountry as string | null) ?? null,
              playerCount: (d.playerCount as number) ?? 0,
              maxPlayers: (d.maxPlayers as number) ?? 8,
              turnLimit: (d.turnLimit as number) ?? 60,
              botDifficulty: (d.botDifficulty as string) ?? 'normal',
              updatedAtMs: updatedAt ? updatedAt.toMillis() : 0,
            };
          }),
        );
      },
      () => setRooms([]),
    );
    // Re-filter stale rooms every 30s even without a new snapshot.
    const timer = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => {
      unsub();
      clearInterval(timer);
    };
  }, [active]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- `tick` re-runs the freshness cutoff
  const cutoff = Date.now() - FRESH_MS;
  void tick;
  // Live games stay listed until they finish (the trigger removes them); only
  // lobbies age out on the freshness heartbeat.
  return rooms.filter((r) => r.status === 'playing' || r.updatedAtMs >= cutoff);
}
